import {ApplicationPolicy, FeeMode} from "../../configuration/configuration.model";

export interface Order {
	orderUuid: string;
	auditUuid: string;
	orderNumber: string;
	orderCorrelationId: string;
	activeOrder: boolean;
	status: OrderStatusEnum;
	company: Company;
	products: Product[];
	contactPerson: ContactPerson;
	termsAndConditions: TermsAndConditions;
	certificateFiles: FileDetails[];
	reportFiles: FileDetails[];
	additionalQuestionsSubmitted: boolean;
	creationTimestamp: Date;
	sentToReviewTimestamp: Date;
	isSalesLead: boolean;
	isSalesLeadArchived: boolean;
	declineInfo: DeclineInfo;
	reviewerChangeComment: string;
	assignedClientService: AssignedDekraEmployee;
	assignedApplicationReviewer: AssignedDekraEmployee;
	assignedSalesPerson: AssignedDekraEmployee;
	opportunityId: string;
	dataCompared: boolean;
	auditNumber: string;
	derivedFromTransfer: boolean;
	salesLeadSentToClientTimestamp: Date;
	billingInformations: BillingInformation;
	clientCountry: string;
	isClientsDataTranslationNeeded: boolean;
	isPricingChanged: boolean;
	takeoverThresholdExceeded: boolean;
}

export interface CaseNotes {
	comment: string;
	onsiteVisitRequired: boolean;
	onsiteVisitDone: boolean;
}

export interface DeclineTransfer {
	reason: string;
	caseNotes: CaseNotes;
}

export type Audit = Order;

export interface AssignedDekraEmployee {
	firstName: string;
	lastName: string;
	fullName: string;
	email: string;
}

export interface DeclineInfo {
	declineEvents: DeclineEvent[];
}

export interface DeclineEvent {
	reason: string;
	timestamp: Date;
	userRoles: UserRole[];
}

export interface UserRole {
	role: string;
}

export interface FileDetails {
	fileUuid: string;
	fileName: string;
	md5: string;
	attribute: DocumentAttribute;
}

export enum DocumentAttribute {
	MARK_FOR_STORE = 'MARK_FOR_STORE',
	MARK_FOR_DELETION = 'MARK_FOR_DELETION'
}

export enum OrderStatusEnum {
	LEAD = 'LEAD',
	NEW = 'NEW',
	DECLINED_BY_CLIENT = 'DECLINED_BY_CLIENT',
	ORDER_CONFIRMATION = 'ORDER_CONFIRMATION',
	UNDER_APPLICATION_REVIEW = 'UNDER_APPLICATION_REVIEW',
	REVIEW_IN_PROGRESS = 'REVIEW_IN_PROGRESS',
	REVIEW_CHANGE_DRAFT = 'REVIEW_CHANGE_DRAFT',
	REVIEW_CHANGE_PROPOSAL = 'REVIEW_CHANGE_PROPOSAL',
	DECLINED_BY_APPLICATION_REVIEWER = 'DECLINED_BY_APPLICATION_REVIEWER',
	ACCEPTED_BY_APPLICATION_REVIEWER = 'ACCEPTED_BY_APPLICATION_REVIEWER',
	REVIEW_CHANGE_PROPOSAL_ACCEPTED_BY_CLIENT = 'REVIEW_CHANGE_PROPOSAL_ACCEPTED_BY_CLIENT',

	// Transfer specific
	UPLOAD_TRANSFER_DOCUMENTS = 'UPLOAD_TRANSFER_DOCUMENTS',
	TRANSFER_CHECK = 'TRANSFER_CHECK',
	DECLINED_BY_TECHNICAL_REVIEWER = 'DECLINED_BY_TECHNICAL_REVIEWER',
	TRANSFER_DECISION = 'TRANSFER_DECISION',
	PRE_TRANSFER_VISIT_NEEDED = 'PRE_TRANSFER_VISIT_NEEDED',
	PRE_TRANSFER_VISIT_FINISHED = 'PRE_TRANSFER_VISIT_FINISHED',
	TRANSFER_DECISION_NEGATIVE = 'TRANSFER_DECISION_NEGATIVE',
	TRANSFER_DECISION_POSITIVE = 'TRANSFER_DECISION_POSITIVE',

	PROPOSAL_CHANGE = 'PROPOSAL_CHANGE', // ?????????? REVIEW_CHANGE_PROPOSAL ?????????

	// Recertification specific
	DRAFT = 'DRAFT',
	PREVIEW = 'PREVIEW',
	FORECASTED = 'FORECASTED',
	CONFIRMED = 'CONFIRMED',
	SENT = 'SENT',
	DECLINED = 'DECLINED',
	CLIENT_CHANGE = 'CLIENT_CHANGE',
	CLOSED = 'CLOSED',

	// CS specific
	AUDIT_EVENT_CONFIRMATION = 'AUDIT_EVENT_CONFIRMATION',
	AUDIT_EVENT_CONFIRMED = 'AUDIT_EVENT_CONFIRMED',
	AUDIT_EVENT_CHANGED = 'AUDIT_EVENT_CHANGED',
	AUDIT_EVENT_CLOSED = 'AUDIT_EVENT_CLOSED',

	EDIT_LEAD = 'EDIT_LEAD',
	SALES_LEAD_DRAFT = 'SALES_LEAD_DRAFT',
	TRANSLATION = 'TRANSLATION'
}

export function getStatusText(status: OrderStatusEnum) {
	if (status == OrderStatusEnum.SENT) {
		return 'PROPOSAL SENT';
	}
	return status != null ? status.replace('_', ' ') : '';
}

export interface ContactPerson {
	firstName: string;
	lastName: string;
	jobTitle: string;
	countryCode: string;
	phoneNumber: string;
	email: string;
	salesperson?: string;
}

export interface TransferQuestions {
	lastAuditEvent: AuditType;
}

export enum AuditType {
	INITIAL_CERTIFICATION = "INITIAL_CERTIFICATION",
	PRE_AUDIT = "PRE_AUDIT",
	STAGE_1 = "STAGE_1",
	STAGE_2 = "STAGE_2",
	SURVEILLANCE_1 = "SURVEILLANCE_1",
	SURVEILLANCE_2 = "SURVEILLANCE_2",
	RECERTIFICATION = "RECERTIFICATION"
}

export interface RecertificationQuestions {
	auditorFirstName: string;
	auditorLastName: string;
	nextAuditEvent: AuditType;
	remoteAudit: boolean;
}

export interface Product {
	accreditationBody: AccreditationBody;
	standard: Standard;
	transferQuestions: TransferQuestions;
	recertificationQuestions: RecertificationQuestions;
	scopes: Scope[];
	employees: number;
	scopeDescriptionNeedsNarrowing: boolean;
	scopeDescription: string;
	employeesWithinScope: number;
	partTimeOrTempEmployeesIncluded: boolean;
	employeesCountDescription: string;
	shifts: number;
	companyDescription: string;
	priceFactors: PriceFactor[];
	estimateQuestions: EstimateQuestion[];
	pricing: ProductPricing;
	additionalQuestions: AdditionalQuestions;
	catastrophicRisk: boolean;
	currentAuditEvent: AuditType;
	followingAuditEvent: AuditType;
	auditorFirstName: string;
	auditorLastName: string;
}

export interface AdditionalQuestions {
	transferQuestions?: AdditionalTransferQuestions;
	initialCertificationQuestions?: AdditionalInitialCertificationQuestions;
	desiredNextAuditDate: Date;
	textQuestions: TextQuestion[];
	binaryQuestions: BinaryQuestion[];
	outsourcedProcesses: string[];
}

export interface TextQuestion {
	type?: TextQuestionType;
	question: string;
	group: AdditionalQuestionGroup;
	answer: string;
	description?: string;
}

export interface BinaryQuestion {
	type?: string;
	question: string;
	answer: boolean;
	description?: string;
	commentMandatoryWithAnswerValueEqualTo: boolean;
}

export interface AdditionalTransferQuestions {
	transferReason: string;
	isCurrentCertificationBodyAccredited: boolean;
	hasOpenNonConformities: boolean;
	hasCorrectiveActionPlans: boolean;
	numberOfMajorNonConformities: number;
	numberOfMinorNonConformities: number;
	lastAuditPerformedDate: Date;
	expirationDate: Date;
}

export interface AdditionalInitialCertificationQuestions {
	preAuditRequested: boolean;
	desiredPreAuditDate: Date;
	desiredStage1AuditDate: Date;
}

export interface ProductPricing {
	annualPricings: AnnualPricing[];
	pricingAdjustments: PricingAdjustments;
	dailyRate: number;
	totalPrice: number;
	totalAuditTime: number;
	risk: Risk;
	complexityFactor: Risk;
	auditEffort: Risk;
	businessComplexity: Risk;
	itComplexity: Risk;
	appliedDiscount: number;
	calculatedDiscount: number;
}

export interface AnnualPricing {
	auditPricings: AuditPricing[];
}

export enum FeeType {
	CERTIFICATION_FEE = 'CERTIFICATION_FEE',
	CERTIFICATION_FEE_PER_EVENT = 'CERTIFICATION_FEE_PER_EVENT',
	ADMINISTRATION_FEE = 'ADMINISTRATION_FEE',
	REGISTRATION_FEE = 'REGISTRATION_FEE',
	REGISTRATION_FEE_PER_EVENT = 'REGISTRATION_FEE_PER_EVENT',
	PRE_AUDIT_FEE = 'PRE_AUDIT_FEE',
	STAGE_1_DAILY_RATE_FEE = 'STAGE_1_DAILY_RATE_FEE',
	STAGE_2_DAILY_RATE_FEE = 'STAGE_2_DAILY_RATE_FEE',
	SURVEILLANCE_1_DAILY_RATE = 'SURVEILLANCE_1_DAILY_RATE',
	SURVEILLANCE_2_DAILY_RATE = 'SURVEILLANCE_2_DAILY_RATE',
	RECERTIFICATION_DAILY_RATE = 'RECERTIFICATION_DAILY_RATE',
	ADMIN_FEE_YEAR_1 = 'ADMIN_FEE_YEAR_1',
	ADMIN_FEE_YEAR_2 = 'ADMIN_FEE_YEAR_2',
	ADMIN_FEE_YEAR_3 = 'ADMIN_FEE_YEAR_3',
	CERTIFICATION_FEE_YEAR_1 = 'CERTIFICATION_FEE_YEAR_1',
	CERTIFICATION_FEE_YEAR_2 = 'CERTIFICATION_FEE_YEAR_2',
	CERTIFICATION_FEE_YEAR_3 = 'CERTIFICATION_FEE_YEAR_3',
}

export const FEES_ONLY = new Set([
	FeeType.ADMIN_FEE_YEAR_1, FeeType.ADMIN_FEE_YEAR_2, FeeType.ADMIN_FEE_YEAR_3,
	FeeType.CERTIFICATION_FEE_YEAR_1, FeeType.CERTIFICATION_FEE_YEAR_2, FeeType.CERTIFICATION_FEE_YEAR_3,
	FeeType.CERTIFICATION_FEE, FeeType.REGISTRATION_FEE, FeeType.ADMINISTRATION_FEE,
	FeeType.CERTIFICATION_FEE_PER_EVENT, FeeType.REGISTRATION_FEE_PER_EVENT,
]);

export const DAILY_RATES_ONLY = new Set([
	FeeType.PRE_AUDIT_FEE,
	FeeType.STAGE_1_DAILY_RATE_FEE,
	FeeType.STAGE_2_DAILY_RATE_FEE,
	FeeType.SURVEILLANCE_1_DAILY_RATE,
	FeeType.SURVEILLANCE_2_DAILY_RATE,
	FeeType.RECERTIFICATION_DAILY_RATE
]);

export interface Fee {
	type: FeeType;
	amount: number;
	applied: boolean;
	mode: FeeMode;
	applicationPolicy: ApplicationPolicy
}

export interface AuditPricing {
	type: AuditType;
	sequenceNumber: number;
	onsiteDays: number;
	offsiteDays: number;
	price: number;
	fees: Fee[];
}

export interface PricingAdjustments {
	dailyRate: number;
	complexityFactor: Risk
	auditEffort: Risk;
	businessComplexity: Risk;
	itComplexity: Risk;
	risk: Risk;
	fees: Fee[];
}

export interface PriceFactor {
	standardName: StandardName;
	type: string;
	question: string;
	positiveAnswer: boolean;
	positiveAnswerFactor: number;
	negativeAnswerFactor: number;
	attachedQuestions: AttachedQuestion[];
}

export interface AttachedQuestion {
	type: string;
	question: string;
	parentQuestionAnswerValue: any;
	questionType: AttachedQuestionQuestionType;
	answer: any;
}

export interface Scope {
	code: string;
	name: string;
	standardName: StandardName;
	questions?: ScopeQuestion[];
}

export interface ScopeQuestion {
	type: string;
	text: string;
	answer: boolean;
}

export function isAudit(order: Order): boolean {
	return [OrderStatusEnum.AUDIT_EVENT_CONFIRMATION, OrderStatusEnum.AUDIT_EVENT_CONFIRMED, OrderStatusEnum.AUDIT_EVENT_CHANGED, OrderStatusEnum.AUDIT_EVENT_CLOSED].includes(order.status);
}

export function getProcessTypeForStandard(standard: Standard) {
	return standard.hasCertificate ?
		(standard.hasDekraCertificate ? ProcessType.RECERTIFICATION : ProcessType.TRANSFER) :
		ProcessType.INITIAL_CERTIFICATION;
}

export function getStandardNames(order: Order): StandardName[] {
	return order.products.map(product => product.standard.name);
}

export function getProcessTypeFor(order: Order): ProcessType {
	if (!order.products) {
		return ProcessType.UNDEFINED;
	}

	return order.products.some(product => getProcessTypeForStandard(product.standard) === ProcessType.TRANSFER) ? ProcessType.TRANSFER :
		(
			order.products.some(product => getProcessTypeForStandard(product.standard) === ProcessType.RECERTIFICATION) ?
				ProcessType.RECERTIFICATION :
				ProcessType.INITIAL_CERTIFICATION
		);
}

export enum AttachedQuestionQuestionType {
	TEXT = "TEXT",
	RADIO = "RADIO"
}

export enum ProcessType {
	INITIAL_CERTIFICATION = "INITIAL_CERTIFICATION",
	RECERTIFICATION = "RECERTIFICATION",
	TRANSFER = "TRANSFER",
	UNDEFINED = "UNDEFINED"
}


export interface Standard {
	name: StandardName;
	hasCertificate: boolean;
	hasDekraCertificate: boolean;
	customCb: boolean;
	otherCbName: string;
}

export enum AccreditationBody {
	ANAB = "ANAB",
    RVA = "RVA"
}

export enum StandardName {
	ISO_9001 = "ISO_9001",
	ISO_14001 = "ISO_14001",
	ISO_45001 = "ISO_45001",
	ISO_27001 = "ISO_27001",
	ISO_27701 = "ISO_27701"
}

export interface Company {
	name: string;
	street: string;
	city: string;
	zipCode: string;
	state: string;
	country: string;
	isMultiSite?: boolean;
	additionalParams?: {[key: string]: string};
}

export interface TermsAndConditions {
	companyName: string;
	firstName: string;
	lastName: string;
	jobTitle: string;
	email: string;
	phone: string;
	countryCode: string;
	estimateAccepted: boolean;
	termsAndConditionsAccepted: boolean;
}

export enum Risk {
	LOW = "LOW",
	MEDIUM = "MEDIUM",
	HIGH = "HIGH",
	LIMITED = "LIMITED",
	NO_RISK = "NO_RISK"
}

export enum EstimateQuestionType {
	LONG_OPTION_SINGLE_CHOICE = "LONG_OPTION_SINGLE_CHOICE"
}

export interface EstimateQuestion {
	order: number;
	type: EstimateQuestionType;
	questionId: string;
	optionIds: string[];
	chosenOptionIds: string[];
	attachedQuestions: AttachedQuestion[];
}

export interface Country {
	code: string;
	name: string;
	administrativeDivisions: string[];
	zipCodeRegExp: string;
	callPrefix: string;
	dependentCountries: Country[];
}

export interface BillingInformation {
	sameCompanyAndContactPersonAsProvided: boolean;
	billingCompany: Company;
	billingContactPerson: ContactPerson;
	isPurchaseNumberRequiredForPayment: boolean;
	purchaseOrderNumber: string;
	coverTravelExpenses: boolean;
	additionalInformation: string;
	initial?: boolean;
}

export enum AdditionalQuestionGroup {
	POTENTIAL_ASPECTS = 'POTENTIAL_ASPECTS',
	POTENTIAL_RISKS = 'POTENTIAL_ASPECTS',
	DEFAULT = 'DEFAULT'
}

export enum TextQuestionType {
	MATERIALS_DESCRIPTION = "MATERIALS_DESCRIPTION",
	PERMITS_IN_EFFECT = "PERMITS_IN_EFFECT",
	SAFETY_CONSIDERATIONS = "SAFETY_CONSIDERATIONS",
	INFORMATION_BEING_PROCESSED_OR_CONTROLLED = "INFORMATION_BEING_PROCESSED_OR_CONTROLLED",
	ISO_27701_CONTROLS_IMPLEMENTED = "ISO_27701_CONTROLS_IMPLEMENTED",
	EMISSION_TO_AIR = "EMISSION_TO_AIR",
	RELEASE_TO_WATER = "RELEASE_TO_WATER",
	RELEASE_TO_LAND = "RELEASE_TO_LAND",
	WASTE_EMISSION = "WASTE_EMISSION",
	PACKAGING = "PACKAGING",
	OTHER_ISO14001 = "OTHER_ISO14001",
	MACHINERY_EQUIPMENT = "MACHINERY_EQUIPMENT",
	TOXIC_SUBSTANCES = "TOXIC_SUBSTANCES",
	RADIATION = "RADIATION",
	WORKLOAD = "WORKLOAD",
	BEHAVIOR_HARASSMENT = "BEHAVIOR_HARASSMENT",
	SITUATIONS = "SITUATIONS",
	OTHER_ISO45001 = "OTHER_ISO45001"
}

export interface UTMParams {
	UTMSource__c: string;
	UTMMedium__c: string;
	UTMCampaign__c: string;
}
